export interface PasswordOptions {
  length: number;
  lowercase: boolean;
  uppercase: boolean;
  digits: boolean;
  symbols: boolean;
  /** Drop characters that are easily misread when typed from a screen: 0/O, 1/l/I, and the like. */
  excludeAmbiguous: boolean;
}

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export const DEFAULT_PASSWORD_OPTIONS: PasswordOptions = {
  length: 24,
  lowercase: true,
  uppercase: true,
  digits: true,
  symbols: true,
  excludeAmbiguous: false,
};

const CHARSETS = {
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  digits: "0123456789",
  symbols: "!@#$%^&*()-_=+[]{};:,.<>?/~",
} as const;

const AMBIGUOUS = new Set("0O1lI|`'\";:,.");

type CharsetKey = keyof typeof CHARSETS;

function selectedCharsets(options: PasswordOptions): string[] {
  return (Object.keys(CHARSETS) as CharsetKey[])
    .filter((key) => options[key])
    .map((key) =>
      options.excludeAmbiguous ? [...CHARSETS[key]].filter((c) => !AMBIGUOUS.has(c)).join("") : CHARSETS[key],
    );
}

/** True when the options select at least one character class. */
export function canGeneratePassword(options: PasswordOptions): boolean {
  return selectedCharsets(options).length > 0;
}

/**
 * A uniform integer in [0, max), by rejection sampling: `byte % max` alone
 * would favour the low values whenever 256 isn't a multiple of `max`.
 */
function randomIndex(max: number): number {
  const limit = 256 - (256 % max);
  const byte = new Uint8Array(1);
  for (;;) {
    globalThis.crypto.getRandomValues(byte);
    if (byte[0] < limit) return byte[0] % max;
  }
}

/**
 * Generates a password from the Web Crypto CSPRNG. Every selected class is
 * guaranteed at least one character — a site that demands "one digit" should
 * never reject what this produced — and the result is shuffled afterwards so
 * those guaranteed characters don't sit at predictable positions.
 */
export function generatePassword(options: PasswordOptions): string {
  const sets = selectedCharsets(options);
  if (sets.length === 0) throw new Error("Select at least one character type.");
  const length = Math.min(PASSWORD_MAX_LENGTH, Math.max(PASSWORD_MIN_LENGTH, Math.floor(options.length)));
  const pool = sets.join("");

  const chars = sets.map((set) => set[randomIndex(set.length)]);
  while (chars.length < length) chars.push(pool[randomIndex(pool.length)]);

  // Fisher–Yates.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
